import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { CanActivateFn, Router } from "@angular/router";
import { of } from "rxjs";
import { APIService } from "./api.service";
import { IActorUser, IState, IStateUser, Permission } from "./api.types";

export const authAdminResolver = () => {
  const auth = inject(AuthService);
  return of(auth.perm('ap-access') || inject(Router).navigateByUrl('/'));
}

export const authUserLoggedResolver = () => {
  const isLogged = inject(AuthService).isLogged;
  return of(isLogged || inject(Router).navigateByUrl('/login'));
}

export const canActivatePermission = (perm: Permission) => {
  return (() => {
    const auth = inject(AuthService);
    return of(auth.perm(perm) || inject(Router).navigateByUrl('/'));
  }) as CanActivateFn;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private api = inject(APIService);
  private router = inject(Router);
  private state?: IState;
  private emptyUser: IStateUser = {
    id: -1,
    name: '',
    balance: 0,
    deployments: 0,
    roles: [],
    permissions: [],
    blocked: false,
    waitlist: false,
    max_deployments: 0
  };
  private roleMap: Map<string, boolean> = new Map();
  private permissions: Map<Permission, boolean> = new Map();
  onBalanceChanged = new EventEmitter<number>();
  onLogout = new EventEmitter<void>();
  /** True when an admin browses the site under another user */
  readonly isImpersonating = signal(false);

  perm(permission: Permission): boolean {
    return !!this.permissions.get(permission);
  }

  get isLogged() {
    return !!this.state?.auth;
  }

  get user() {
    return this.state?.user || this.emptyUser;
  }

  /** Admin who started the impersonation session */
  get actorUser(): IActorUser | undefined {
    return this.state?.actor_user;
  }

  async initializeUserState() {
    return this.api.getState()
      .then(state => {
        this.state = state;
        this.isImpersonating.set(!!state.is_impersonating);
        this.buildRoleMap();
        this.buildPermissionsMap();
      });
  }

  /**
   * Start viewing the site under the given user.
   * Session cookie is replaced by the response, so the app is fully reloaded to refresh all data.
   */
  async impersonate(userId: number) {
    await this.api.startImpersonation(userId);
    location.href = '/';
  }

  /** Return to the admin session and reload the app to refresh all data */
  async exitImpersonation() {
    await this.api.stopImpersonation();
    location.href = '/admin/members';
  }

  private buildRoleMap() {
    this.roleMap = new Map();
    if (!this.state?.user) {
      return;
    }
    this.state.user.roles.forEach(r => {
      this.roleMap.set(r, true);
    });
  }

  async login(doRegistration = false, redirectUrl = '') {
    const resp = await this.api.getOAuthUrl({
      url: redirectUrl || location.href,
      registration: doRegistration ? 1 : 0
    });

    location.href = resp.url;
  }

  register(redirectUrl = '') {
    void this.login(true, redirectUrl);
  }

  async logout() {
    try {
      await this.api.doLogout()
      this.roleMap = new Map();
      this.permissions = new Map();
      if (this.state) {
        this.state.auth = false;
        this.state.user = this.emptyUser;
      }
      this.onLogout.emit();
      void this.router.navigateByUrl('/login');
    } catch (e) {
      console.error(e);
    }
  }

  private buildPermissionsMap() {
    this.permissions = new Map();
    if (!this.state?.user) {
      return;
    }
    this.state.user.permissions.forEach(p => {
      this.permissions.set(p, true);
    })
  }
}
